import apiClient, { API_BASE_URL } from './apiClient';
import type {
  IceCandidatePayload,
  RealtimeSessionResponse,
  RealtimePrescriptionResponsePayload,
  SignalingOfferResponse,
  VoiceChatResponse,
} from '../types';

export interface CreateRealtimeSessionPayload {
  conversation_id?: string;
  metadata?: Record<string, unknown>;
}

const BASE_PATHNAME = (() => {
  try {
    const url = new URL(API_BASE_URL);
    return url.pathname.replace(/\/+$/, '') || '/';
  } catch (error) {
    console.warn('无法解析 API 基础路径，使用默认值 /', error);
    return '/';
  }
})();

const normalizeRealtimePath = (path: string | undefined, fallback: string): string => {
  const fallbackWithSlash = fallback.startsWith('/') ? fallback : `/${fallback}`;
  const input = path && path.trim().length > 0 ? path.trim() : fallbackWithSlash;
  const ensured = input.startsWith('/') ? input : `/${input}`;
  if (BASE_PATHNAME !== '/' && ensured.startsWith(BASE_PATHNAME)) {
    const remainder = ensured.slice(BASE_PATHNAME.length) || '/';
    return remainder.startsWith('/') ? remainder : `/${remainder}`;
  }
  return ensured;
};

export const createRealtimeSession = async (
  payload: CreateRealtimeSessionPayload = {},
): Promise<RealtimeSessionResponse> => {
  const response = await apiClient.post<RealtimeSessionResponse>('/realtime/session', payload);
  return response.data;
};

export interface SubmitRealtimeOfferPayload {
  token: string;
  sdp: string;
  type: string;
  pcId?: string | null;
  restartPc?: boolean;
  signalingPath?: string;
}

export const submitRealtimeOffer = async (
  payload: SubmitRealtimeOfferPayload,
): Promise<SignalingOfferResponse> => {
  const path = normalizeRealtimePath(payload.signalingPath, '/realtime/signaling/offer');
  const requestBody: Record<string, unknown> = {
    token: payload.token,
    sdp: payload.sdp,
    type: payload.type,
    restart_pc: payload.restartPc ?? false,
  };
  if (payload.pcId) {
    requestBody.pc_id = payload.pcId;
  }
  const response = await apiClient.post<SignalingOfferResponse>(path, requestBody);
  return response.data;
};

export interface SubmitIceCandidatesPayload {
  token: string;
  pcId: string;
  candidates: IceCandidatePayload[];
  icePath?: string;
}

export const submitRealtimeIceCandidates = async (
  payload: SubmitIceCandidatesPayload,
): Promise<void> => {
  if (!payload.candidates.length) {
    return;
  }
  const path = normalizeRealtimePath(payload.icePath, '/realtime/signaling/ice');
  await apiClient.post(path, {
    token: payload.token,
    pc_id: payload.pcId,
    candidates: payload.candidates.map((candidate) => ({
      candidate: candidate.candidate,
      sdp_mid: candidate.sdp_mid,
      sdp_mline_index: candidate.sdp_mline_index,
    })),
  });
};

export interface RealtimePrescriptionRequestPayload {
  notes?: string;
  include_turns?: number;
  patient_info?: Record<string, unknown>;
}

export const requestRealtimePrescription = async (
  sessionId: string,
  payload: RealtimePrescriptionRequestPayload = {},
): Promise<RealtimePrescriptionResponsePayload> => {
  const response = await apiClient.post<RealtimePrescriptionResponsePayload>(
    `/realtime/session/${sessionId}/prescription`,
    payload,
  );
  return response.data;
};

export interface VoiceRoundtripOptions {
  conversationId?: string | null;
  sessionId?: string | null;
  shouldSpeak?: boolean;
  mimeType?: string;
}

export const submitVoiceRoundtrip = async (
  audioBlob: Blob,
  {
    conversationId,
    sessionId,
    shouldSpeak = true,
    mimeType,
  }: VoiceRoundtripOptions = {},
): Promise<VoiceChatResponse> => {
  const formData = new FormData();
  const fileName = `voice_${Date.now()}.webm`;
  const mediaType = mimeType || audioBlob.type || 'audio/webm';
  const file = new File([audioBlob], fileName, { type: mediaType });
  formData.append('file', file);
  if (conversationId) {
    formData.append('conversation_id', conversationId);
  }
  if (sessionId) {
    formData.append('session_id', sessionId);
  }
  formData.append('should_speak', shouldSpeak ? 'true' : 'false');

  const response = await apiClient.post<VoiceChatResponse>('/chat/voice-roundtrip', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};
